import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RailRowMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { CanvasCommentIdentityAvatar } from "@/pages/canvas/comments/CanvasCommentPreviewCard";
import {
  emitCanvasOpenCommentThread,
  emitCanvasResolveCommentThread,
} from "@/pages/canvas/comments/CanvasCommentsContext";
import { cn } from "@/utils";
import { useUser } from "@liveblocks/react";
import { Timestamp } from "@liveblocks/react-ui/primitives";
import { CheckCircle2, CircleDot, Crosshair, Link2Off } from "lucide-react";
import { useState } from "react";
import type { CommentRailItem } from "./canvasRailCommentsModel";
import { ExplorerRow } from "./rows/ExplorerRow";

/**
 * Comments scope: every thread on the canvas, unresolved first and newest
 * first inside each group. Rows come from `useCanvasRailComments`, which the
 * rail also reads to decide between the list and the zero state.
 */
export function CanvasRailComments({ items }: { items: CommentRailItem[] }) {
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    item: CommentRailItem;
  } | null>(null);

  return (
    <>
      {items.map((item) => (
        <CommentRow
          key={item.threadId}
          item={item}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY, item });
          }}
        />
      ))}

      {menu && (
        <RailRowMenu
          kebab={false}
          label="Comment"
          open
          onOpenChange={(o) => {
            if (!o) setMenu(null);
          }}
          point={{ x: menu.x, y: menu.y }}
        >
          <DropdownMenuItem onClick={() => locate(menu.item)}>
            <Crosshair />
            Locate on canvas
          </DropdownMenuItem>
          {menu.item.resolved && (
            <DropdownMenuItem
              onClick={() =>
                emitCanvasResolveCommentThread(menu.item.threadId, false)
              }
            >
              <CircleDot />
              Unresolve
            </DropdownMenuItem>
          )}
        </RailRowMenu>
      )}
    </>
  );
}

/** Locating a comment centres its pin, not a node: an anchored pin has no node
 *  of its own, and a detached one has only the frozen coordinate. */
function locate(item: CommentRailItem) {
  window.dispatchEvent(
    new CustomEvent("canvas:focus-point", {
      detail: { x: item.pin.x, y: item.pin.y, keepZoom: true },
    }),
  );
  emitCanvasOpenCommentThread(item.threadId);
}

function CommentRow({
  item,
  onContextMenu,
}: {
  item: CommentRailItem;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const author = useUser(item.lastAuthorId ?? "");

  return (
    <ExplorerRow
      leading={
        item.resolved ? (
          <CheckCircle2 className="size-4 text-n-40" />
        ) : (
          // Avatar-forward, like the pin's preview card — a comment is
          // identified by who wrote it. The row reads last activity, so this
          // is the last author, not the thread's opener.
          <CanvasCommentIdentityAvatar
            name={author.user?.name}
            src={author.user?.avatar}
            className="size-4"
          />
        )
      }
      title={
        <span className={cn(item.resolved && "text-n-40")}>
          {author.user?.name ?? "Comment"}
        </span>
      }
      subtitle={
        <span className="flex items-center gap-1">
          {item.commentCount === 1
            ? "1 comment"
            : `${item.commentCount} comments`}
          {item.lastActivityAt && (
            <>
              {" · "}
              <Timestamp date={item.lastActivityAt} />
            </>
          )}
          {item.orphaned && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link2Off className="size-3 text-n-40" />
              </TooltipTrigger>
              <TooltipContent>Detached — its object was deleted</TooltipContent>
            </Tooltip>
          )}
        </span>
      }
      onClick={() => locate(item)}
      onContextMenu={onContextMenu}
    />
  );
}
